import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { PrescriptionsService } from 'src/app/services/prescriptions/prescriptions.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import {
  PrescriptionsApiActions,
  PrescriptionsErrorsAction,
  PrescriptionsStoreActions
} from 'src/app/store/actions/prescriptions.action';
import { GeneralError } from 'vidis-common';
import { selectFilter, selectPagination } from '../selectors/prescriptions.selector';
import { selectPatientSSIN, selectUserStateUserRole } from '../selectors/user.selector';

@Injectable()
export class PrescriptionsEffects {
  constructor(
    private actions$: Actions,
    private prescriptionsService: PrescriptionsService,
    private toastService: ToastService,
    private store: Store,
    private router: Router,
  ) {
  }

  private readonly allowedRoles = ['patient', 'dentist', 'midwife', 'physician'];

  fetchPrescriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionsApiActions.fetchPrescriptionsByPage),
      withLatestFrom(
        this.store.select(selectUserStateUserRole),
        this.store.select(selectPatientSSIN),
        this.store.select(selectFilter),
        this.store.select(selectPagination)
      ),
      switchMap(([action, role, ssin, filter, pagination]) => {
        if (this.allowedRoles.includes(role) && ssin !== undefined) {
          return this.processPrescriptionsRequest(
            this.prescriptionsService.getPrescriptions(ssin, filter, pagination),
            prescriptionResult => PrescriptionsStoreActions.setPrescriptions({ prescriptionResult })
          );
        }
        return EMPTY;
      })
    )
  );

  fetchCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionsApiActions.fetchCount),
      withLatestFrom(
        this.store.select(selectUserStateUserRole),
        this.store.select(selectPatientSSIN)
      ),
      switchMap(([action, role, ssin]) => {
        if (this.allowedRoles.includes(role) && ssin !== undefined) {
          return this.processPrescriptionsRequest(
            this.prescriptionsService.countPrescriptions(ssin),
            countPrescriptionResult => PrescriptionsStoreActions.setPrescriptionsCount({ countPrescriptionResult })
          );
        }
        return EMPTY;
      })
    )
  );

  refreshPrescriptionData$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsApiActions.fetchSinglePrescription),
    mergeMap(content => this.store.select(selectPatientSSIN)
      .pipe(
        first(),
        switchMap(ssin => this.prescriptionsService.getPrescription(ssin, content.ridCode)),
        map(prescription => PrescriptionsStoreActions.setSinglePrescription({ prescription }))
      )
    )
  ));

  triggerFetchPrescriptionsByPage$ = createEffect(() => this.actions$.pipe(
    ofType(
      PrescriptionsStoreActions.updateFiltering,
      PrescriptionsStoreActions.updatePagination,
      PrescriptionsStoreActions.removeOneFilter,
    ),
    map(() => PrescriptionsApiActions.fetchPrescriptionsByPage())
  ));

  makeReservation$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsApiActions.makeReservation),
    switchMap(({ ridCode, reservationRequest, pharmacy, closeDialog }) =>
      this.prescriptionsService.makeReservationForPrescription(ridCode, reservationRequest)
        .pipe(
          map(() => {
            if (closeDialog) {
              closeDialog();
            }

            return PrescriptionsStoreActions.updatePrescription({
              ridCode,
              reservationPharmacy: pharmacy
            });
          }),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = error.error?.code === 'VIDIS_3140' ? 'error_3140' : 'error';
            return of(PrescriptionsErrorsAction.displayErrorToaster({ errorMessage: 'prescription-page.details.reservation-dialog.' + errorMessage }));
          })
        ),
    )
  ));

  editPrescriptions$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsApiActions.editPrescription),
    mergeMap(({ editPrescriptionRequest, pharmacy, closeDialog }) => this.store.select(selectPatientSSIN)
      .pipe(
        take(1),
        switchMap(ssin => this.prescriptionsService.editPrescriptions({
          items: [{
            ...editPrescriptionRequest,
            ssin
          }]
        }).pipe(
          map(() => {
            if (closeDialog) {
              closeDialog();
            };

            return PrescriptionsStoreActions.updatePrescription({
              ridCode: editPrescriptionRequest.ridCode,
              visibilityPrescriber: editPrescriptionRequest.visibilityPrescriber,
              visibilityPharmacy: editPrescriptionRequest.visibility !== undefined
                ? editPrescriptionRequest.visibility === null
                  ? null
                  : {
                    pharmacy: editPrescriptionRequest.visibility === 'locked' ? undefined : pharmacy,
                    locked: editPrescriptionRequest.visibility === 'locked'
                  }
                : undefined
            });
          }),
          catchError((error: HttpErrorResponse) => of(PrescriptionsErrorsAction.displayErrorToaster({
            errorMessage: error.error?.code === 'VIDIS_3100'
              ? 'prescription-page.details.visibility-dialog.vidis_3100_error'
              : 'prescription-page.details.visibility-dialog.error'
          })))
        ))
      )
    )
  ));

  cancelReservationForPrescription$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsApiActions.cancelReservation),
    mergeMap(({ridCode}) => this.store.select(selectPatientSSIN)
      .pipe(
        first(),
        switchMap(ssin => this.prescriptionsService.cancelReservationForPrescription(ridCode, ssin)
          .pipe(
            map(() => PrescriptionsStoreActions.removeReservation({ridCode: ridCode})),
            catchError(() => of(PrescriptionsErrorsAction.displayErrorToaster({errorMessage: 'prescription-page.details.cancel-reservation-dialog.cancellation-failed-error'})))
          ))
      ))
  ));

  deletePrescription$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsApiActions.deletePrescription),
    mergeMap(({ deletePrescriptionRequest, closeDialog }) => this.prescriptionsService.deletePrescriptions({
      items: [deletePrescriptionRequest]
    }).pipe(
      map(() => {
        if (closeDialog) {
          closeDialog();
        }
        this.router.navigate(['/', 'prescriptions', 'overview']);
        return PrescriptionsApiActions.fetchPrescriptionsByPage();
      }),
      catchError(() => of(PrescriptionsErrorsAction.displayErrorToaster({
        errorMessage:'prescription-page.delete-dialog.error'
      })))
    )
    )
  ));

  showErrorToaster$ = createEffect(() => this.actions$.pipe(
    ofType(PrescriptionsErrorsAction.displayErrorToaster),
    map(({ errorMessage }) => this.toastService.showError(errorMessage))
  ), { dispatch: false });

  private handleError(err: HttpErrorResponse): Observable<any> {
    let prescriptionsErrors: GeneralError = { code: 'general' };
    if (err.status === 0) {
      prescriptionsErrors.code = 'network_error';
    } else if (err?.error?.code?.includes('VIDIS')) {
      prescriptionsErrors = err.error;
    }
    prescriptionsErrors.traceId = err.headers?.get('trace-id');
    return of(PrescriptionsStoreActions.setPrescriptions({
      prescriptionResult: { errors: [prescriptionsErrors] },
    }));
  }

  private processPrescriptionsRequest<T>(
    observable$: Observable<T>,
    successAction: (result: T) => any
  ): Observable<any> {
    return observable$.pipe(
      switchMap(result => of(successAction(result))),
      catchError(err => this.handleError(err))
    );
  }
}
